// Angular
import { Injectable } from '@angular/core';

// Third party
import { Observable } from 'rxjs';

// Local library
import {
  IACHRequest,
  IAddACHAccountRequest,
  IBaseResponseSlim,
  ICreditCardTransactionRequest,
  ICryptoDepositRequest,
  IDeleteACHAccountRequest,
  IEprotectCardInformationRequest,
  IGetAllACHAccountsResponse,
  IGetCustomerLimitResponse,
  IMoneyPakDepositRequest,
  IPayNearMeAuthRequest,
  IPayNearMeConfRequest
} from '@amwager/util-ts';

// Local file
import { HttpService } from '../services';



// TODO: write tests
@Injectable()
export class TransactionAPIService {
  private readonly API_URL = '/api/Transaction';

  constructor(
    private readonly httpService: HttpService,
  ) {}


  /**
   * GET API
   */

  /**
   * @description
   * PayNearme Authorization Call
   *
   * @param requests IPayNearMeAuthRequest
   * @returns Observable<{}>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  payNearMeAuth$(requests: IPayNearMeAuthRequest): Observable<{}> {
    return this.httpService.get$({
      api: `${this.API_URL}/PayNearMeAuth`,
      params: this.httpService.objToHttpParams(requests as unknown as Record<string, unknown>)
    });
  }

  /**
   * @description
   * PayNearme Confirmation Call
   *
   * @param requests IPayNearMeConfRequest
   * @returns Observable<{}>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  payNearMeConf$(requests: IPayNearMeConfRequest): Observable<{}> {
    return this.httpService.get$({
      api: `${this.API_URL}/PayNearMeConf`,
      params: this.httpService.objToHttpParams(requests as unknown as Record<string, unknown>)
    });
  }

  /**
   *
   * @returns Observable<{}>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  eprotectInformation$(): Observable<{}> {
    return this.httpService.get$({
      api: `${this.API_URL}/EprotectInformation`
    });
  }

  /**
   * @description
   * Gets all cards approved for withdrawls.
   *
   * @returns Observable<{}>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  eprotectInformationForWithdraw$(): Observable<{}> {
    return this.httpService.get$({
      api: `${this.API_URL}/EprotectInformationForWithdraw`
    });
  }

  /**
   *
   * @param method string
   * @param isDeposit boolean
   * @param amount number
   * @returns Observable<ITransactionFeeResponse>
   */
  getTransactionFee$(method: string = 'CreditCard', isDeposit: boolean = true, amount: number)
    // eslint-disable-next-line @typescript-eslint/ban-types
    : Observable<{}> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetTransactionFee`,
      params: this.httpService.objToHttpParams({
        method,
        isDeposit,
        amount
      })
    });
  }

  /**
   *
   * @returns Observable<{}>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  getPendingACH$(): Observable<{}> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetPendingACH`
    });
  }


  /**
   * POST API
   */

  /**
   *
   * @param request IEprotectCardInformationRequest
   * @returns Observable<{}>
   */
  eprotectInformationPost$(request: IEprotectCardInformationRequest)
    // eslint-disable-next-line @typescript-eslint/ban-types
    : Observable<{}> {
    return this.httpService.post$({
      api: `${this.API_URL}/EprotectInformation`,
      body: request
    });
  }

  /**
   * @description
   * Make a deposit using credit card
   *
   * @param request ICreditCardTransactionRequest
   * @returns Observable<{}>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  creditCardDeposit$(request: ICreditCardTransactionRequest): Observable<{}> {
    return this.httpService.post$({
      api: `${this.API_URL}/CreditCardDeposit`,
      body: request
    });
  }

  // /**
  //  * @description
  //  * Make a deposit using promo code
  //  *
  //  * @param promoCode string
  //  * @returns Observable<{}>
  //  */
  // // eslint-disable-next-line @typescript-eslint/ban-types
  // promoCodeDeposit$(promoCode: string): Observable<{}> {
  //   return this.httpService.post$({
  //     api: `${this.API_URL}/PromoCodeDeposit`,
  //     params: this.httpService.objToHttpParams({
  //       promoCode
  //     })
  //   });
  // }

  /**
   * @description
   * Make a deposit using moneypak
   *
   * @param request IMoneyPakDepositRequest
   * @returns Observable<{}>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  moneyPakDeposit$(request: IMoneyPakDepositRequest): Observable<{}> {
    return this.httpService.post$({
      api: `${this.API_URL}/MoneyPakDeposit`,
      body: request
    });
  }

  /**
   * @description
   * Make a deposit using crypto
   *
   * @param request ICryptoDepositRequest
   * @returns Observable<{}>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  cryptoDeposit$(request: ICryptoDepositRequest): Observable<{}> {
    return this.httpService.post$({
      api: `${this.API_URL}/CryptoDeposit`,
      body: request
    });
  }

  /**
   * @description
   * Make a withdraw using credit card
   *
   * @param request ICreditCardTransactionRequest
   * @returns Observable<{}>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  creditCardWithdraw$(request: ICreditCardTransactionRequest): Observable<{}> {
    return this.httpService.post$({
      api: `${this.API_URL}/CreditCardWithdraw`,
      body: request
    });
  }

  /**
   * Make a deposit using promo code
   *
   * @param promoCode string
   * @returns Observable<{}>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  promoCodeDeposit$(promoCode: string): Observable<{}> {
    return this.httpService.post$({
      api: `${this.API_URL}/PromoCodeDeposit`,
      body: {PromoCode: promoCode}
    });
  }

  /**
   * @description
   * Make a withdrawal using Automated Clearing House bank system.
   *
   * @param requests IACHRequest
   * @returns Observable<IBaseResponseSlim>
   */
  aCHWithdrawal$(requests: IACHRequest): Observable<IBaseResponseSlim> {
    return this.httpService.post$({
      api: `${this.API_URL}/ACHWithdrawal`,
      body: requests
    })
  }



  /**
   * DELETE API
   */

  /**
   *
   * @param orderId string
   * @returns Observable<{}>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  eprotectInformationDelete$(orderId: string): Observable<{}> {
    return this.httpService.delete$({
      api: `${this.API_URL}/EprotectInformation`,
      params: this.httpService.objToHttpParams({
        orderId
      })
    });
  }

  // ach

  /**
   * @description
   * Get customer's ACH bank accounts
   *
   * @returns Observable<IGetAllACHAccountsResponse>
   */
  getAllACHAccounts$(): Observable<IGetAllACHAccountsResponse> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetAllACHAccounts`,
    });
  }

  /**
   * @description
   * Add ACH bank account details
   *
   * @param requests IAddACHAccountRequest
   * @returns Observable<IBaseResponseSlim>
   */
  addACHAccount$(requests: IAddACHAccountRequest)
    : Observable<IBaseResponseSlim> {
    return this.httpService.post$({
      api: `${this.API_URL}/AddACHAccount`,
      body: requests
    })
  }

  /**
   * @description
   * Delete ACH bank account details
   *
   * @param requests IDeleteACHAccountRequest
   * @returns Observable<IBaseResponseSlim>
   */
  deleteACHAccount$(requests: IDeleteACHAccountRequest)
    : Observable<IBaseResponseSlim> {
    return this.httpService.delete$({
      api: `${this.API_URL}/DeleteACHAccount`,
      // api: `${this.API_URL}/DeleteACHAccount`+'?ACHAccountId='+requests.ACHAccountId,
      // params: JSON.stringify({
      //   'ACHAccountId': requests.ACHAccountId
      // })
      params: this.httpService.objToHttpParams({
        'ACHAccountId': requests.ACHAccountId
      })
      // body: requests
      // body: JSON.stringify(requests)
      // body: JSON.stringify({
      //   ACHAccountId: Number(requests.ACHAccountId)
      // })
    })
  }

  /**
   * @description
   * Get the ach limits of the requesting customer
   *
   * @returns Observable<IGetCustomerLimitResponse>
   */
  getFundingLimits$(): Observable<IGetCustomerLimitResponse> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetFundingLimits`
    });
  }

  /**
   * @description
   * Make a deposit via Automated Clearing House bank system.
   *
   * @param requests IACHRequest
   * @returns Observable<IBaseResponseSlim>
   */
  aCHDeposit$(requests: IACHRequest): Observable<IBaseResponseSlim> {
    return this.httpService.post$({
      api: `${this.API_URL}/ACHDeposit`,
      body: requests
    })
  }

}
